import type { LucideIcon } from "lucide-react";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export type RangeValue = {
  min?: number;
  max?: number;
  mode?: "single" | "range";
};

type Props = {
  placeholder: string;
  onChange: (value: RangeValue | null) => void;
  value: RangeValue | null;
  icon?: LucideIcon;
  initialMode?: "single" | "range";
  singleTabTitle?: string;
  rangeTabTitle?: string;
  singleLabel?: string;
  minLabel?: string;
  maxLabel?: string;
  singlePlaceholder?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  highlightWhenSelected?: boolean;
};

export const RangeInput = ({
  placeholder,
  onChange,
  value,
  icon: Icon,
  initialMode = "single",
  singleTabTitle,
  rangeTabTitle,
  singleLabel,
  minLabel,
  maxLabel,
  singlePlaceholder,
  minPlaceholder,
  maxPlaceholder,
  highlightWhenSelected = false,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"single" | "range">(initialMode);
  const [minValue, setMinValue] = useState<number | null>(value?.min ?? null);
  const [maxValue, setMaxValue] = useState<number | null>(value?.max ?? null);
  const [sliderValues, setSliderValues] = useState<
    [number | null, number | null]
  >([value?.min ?? null, value?.max ?? null]);

  useEffect(() => {
    setMinValue(value?.min ?? null);
    setMaxValue(value?.max ?? null);
    setSliderValues([value?.min ?? null, value?.max ?? null]);
  }, [value]);

  const handleOnChange = (value: number | (number | null)[] | null) => {
    let formValue: RangeValue | null = null;

    if (value === null) {
      formValue = null;
    } else if (Array.isArray(value)) {
      // Handle array values (range mode)
      const [minVal, maxVal] = value;
      if (minVal !== null || maxVal !== null) {
        formValue = {
          min: minVal ?? undefined,
          max: maxVal ?? undefined,
          mode: "range",
        };
      }
    } else if (!Array.isArray(value)) {
      formValue = {
        min: value,
        mode: "single",
      };
    }

    onChange(formValue);
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numValue = inputValue === "" ? null : Number.parseFloat(inputValue);
    setMinValue(numValue);
    setSliderValues([numValue, sliderValues[1]]);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numValue = inputValue === "" ? null : Number.parseFloat(inputValue);
    setMaxValue(numValue);
    setSliderValues([sliderValues[0], numValue]);
  };

  const handleClearAll = () => {
    setMinValue(null);
    setMaxValue(null);
    setSliderValues([null, null]);
    handleOnChange(null);
    setOpen(false);
  };

  const handlePopoverOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);

    // Trigger onChange when popover closes
    if (!isOpen) {
      let formValue: RangeValue | null = null;

      if (mode === "single") {
        if (minValue !== null) {
          formValue = {
            min: minValue,
            mode: "single",
          };
        }
      } else {
        if (minValue !== null || maxValue !== null) {
          formValue = {
            min: minValue ?? undefined,
            max: maxValue ?? undefined,
            mode: "range",
          };
        }
      }

      onChange(formValue);
    }
  };

  const formatDisplayValue = () => {
    const formatPreviewNumber = (n: number | null) =>
      n === null || n === undefined ? "" : String(n).replace(".", ",");

    if (mode === "single") {
      return minValue !== null
        ? `${formatPreviewNumber(minValue)}`
        : placeholder;
    }
    if (minValue === null && maxValue === null) {
      return placeholder;
    }

    if (minValue === null && maxValue !== null) {
      return `< ${formatPreviewNumber(maxValue)}`;
    }
    if (minValue !== null && maxValue === null) {
      return `> ${formatPreviewNumber(minValue)}`;
    }

    return `${formatPreviewNumber(minValue)} - ${formatPreviewNumber(maxValue)}`;
  };

  const hasValue = () => {
    if (mode === "single") {
      return minValue !== null;
    }
    return minValue !== null || maxValue !== null;
  };

  return (
    <div className="flex">
      <Popover open={open} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-auto text-muted-foreground justify-start gap-1 font-normal",
              hasValue() && "text-primary",
              hasValue() && highlightWhenSelected && "bg-secondary",
            )}
          >
            {Icon && <Icon className="h-4 w-4" />}
            {formatDisplayValue()}
            {hasValue() && (
              <X
                className="w-4 h-4 opacity-50 hover:opacity-100 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <div className="p-4">
            <Tabs
              value={mode}
              onValueChange={(value) => setMode(value as "single" | "range")}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="range">
                  {rangeTabTitle || t("range_input.range")}
                </TabsTrigger>
                <TabsTrigger value="single">
                  {singleTabTitle || t("range_input.single")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="range">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min" className="text-sm">
                      {minLabel || t("range_input.min")}
                    </Label>
                    <Input
                      id="min"
                      type="number"
                      value={minValue ?? ""}
                      onChange={handleMinChange}
                      placeholder={minPlaceholder || "Min value"}
                    />
                  </div>
                  <div>
                    <Label htmlFor="max" className="text-sm">
                      {maxLabel || t("range_input.max")}
                    </Label>
                    <Input
                      id="max"
                      type="number"
                      value={maxValue ?? ""}
                      onChange={handleMaxChange}
                      placeholder={maxPlaceholder || "Max value"}
                    />
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="single">
                <div>
                  <Label htmlFor="min" className="text-sm">
                    {singleLabel || placeholder}
                  </Label>
                  <Input
                    id="min"
                    type="number"
                    value={minValue ?? ""}
                    onChange={handleMinChange}
                    placeholder={singlePlaceholder || "Enter value"}
                  />
                </div>
              </TabsContent>
            </Tabs>

            {hasValue() && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                  className="w-full"
                >
                  {t("range_input.clear_all", "Clear All")}
                </Button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
